import { ScrollableWithBackToTopHandle } from '@app/core/ui/ScrollableWithBackToTop';
import { nlpIntegration } from '@mi-front/api-clients';
import { RefObject, useEffect, useRef, useState } from 'react';

/**
 * Tracks which topics are "new" (arrived after first load) and which have been viewed in detail.
 * Updates the "N temas nuevos" count when the user is scrolled down and new topics arrive.
 * Returns newlyArrivedTopicIds and viewedInDetailTopicIds for list item chips and animation.
 */
export function useLiveTopicsNewArrivals(
  topics: nlpIntegration.TopicOutDto[],
  isSuccess: boolean,
  selectedCategories: string[],
  selectedTopicId: string | null,
  scrollableRef: RefObject<ScrollableWithBackToTopHandle | null>,
  setNewTopicsCount: (fn: (n: number) => number) => void,
) {
  const [viewedInDetailTopicIds, setViewedInDetailTopicIds] = useState<Set<string>>(
    () => new Set(),
  );
  const [newlyArrivedTopicIds, setNewlyArrivedTopicIds] = useState<Set<string>>(() => new Set());

  const prevTopicIdsRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    if (!isSuccess || topics.length === 0) {
      return;
    }

    const currentIds = new Set(topics.map((t) => t.id));
    const hadPrevious = prevTopicIdsRef.current.size > 0;
    const newIds = topics.filter((t) => !prevTopicIdsRef.current.has(t.id)).map((t) => t.id);

    if (hadPrevious && newIds.length > 0) {
      setNewlyArrivedTopicIds((prev) => {
        const next = new Set(prev);
        newIds.forEach((id) => next.add(id));
        return new Set([...next].filter((id) => currentIds.has(id)));
      });
      if (scrollableRef.current?.getIsAtTop() === false) {
        const filteredIds =
          selectedCategories.length === 0
            ? currentIds
            : new Set(
                topics
                  .filter((t) => t.category != null && selectedCategories.includes(t.category))
                  .map((t) => t.id),
              );
        const newIdsInFilter = newIds.filter((id) => filteredIds.has(id)).length;
        setNewTopicsCount((n) => n + newIdsInFilter);
      }
    } else {
      setNewlyArrivedTopicIds((prev) => new Set([...prev].filter((id) => currentIds.has(id))));
    }

    prevTopicIdsRef.current = currentIds;
  }, [isSuccess, topics, selectedCategories, setNewTopicsCount, scrollableRef]);

  useEffect(() => {
    if (selectedTopicId != null) {
      setViewedInDetailTopicIds((prev) =>
        prev.has(selectedTopicId) ? prev : new Set(prev).add(selectedTopicId),
      );
    }
  }, [selectedTopicId]);

  return { newlyArrivedTopicIds, viewedInDetailTopicIds };
}
